import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Users, Settings, LogOut, HeartHandshake } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { to: "/dashboard", label: "Patients", icon: Users },
    { to: "/settings", label: "Paramètres", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/80 backdrop-blur-xl border-b border-[#EAE6DF]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="app-logo">
            <div className="w-9 h-9 rounded-xl bg-[#C07761] flex items-center justify-center">
              <HeartHandshake className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading font-bold text-lg text-[#2D2C28] tracking-tight">
              Séance<span className="text-[#C07761]">Suivi</span>
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname.startsWith(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    active
                      ? "bg-[#C07761] text-white"
                      : "text-[#757169] hover:bg-[#EAE6DF]/60"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{item.label}</span>
                </Link>
              );
            })}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-2 outline-none" data-testid="user-menu-trigger">
                  <Avatar className="w-9 h-9 border border-[#EAE6DF]">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#7B968B] text-white text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-[#2D2C28] truncate">{user?.name}</p>
                  <p className="text-xs text-[#757169] truncate">{user?.email}</p>
                </div>
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  data-testid="menu-settings"
                  className="cursor-pointer"
                >
                  <Settings className="w-4 h-4 mr-2" /> Paramètres SMTP
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="logout-btn"
                  className="cursor-pointer text-[#B56B6B]"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 fade-up">{children}</main>
    </div>
  );
};

export default AppLayout;
